import type {
  ComponentPropsWithoutRef,
  FC,
  ReactElement,
  ReactNode,
} from 'react';
import { CircleAlertIcon } from 'src/components/media-and-icons/icons/circle-alert';
import { CircleXIcon } from 'src/components/media-and-icons/icons/circle-x';
import { InfoIcon } from 'src/components/media-and-icons/icons/info';
import { cn } from 'src/utils/cn';

type AlertProps = {
  type: 'info' | 'warning' | 'error';
  title?: ReactNode;
} & ComponentPropsWithoutRef<'div'>;

const icons: Record<AlertProps['type'], ReactElement> = {
  info: <InfoIcon size="md" />,
  warning: <CircleAlertIcon size="md" />,
  error: <CircleXIcon size="md" />,
};

const classNames: Record<AlertProps['type'], string> = {
  info: cn('bg-background-support-info-minimal text-foreground-support-info'),
  warning: cn(
    'bg-background-support-warning-minimal text-foreground-support-warning'
  ),
  error: cn(
    'bg-background-support-error-minimal text-foreground-support-error'
  ),
};

export const Alert: FC<AlertProps> = ({ type, children, title, ...rest }) => {
  return (
    <div
      className={cn(
        classNames[type],
        `grid grid-cols-[auto_1fr] gap-lg rounded-xs px-xl py-lg`
      )}
      data-testid={`alert-${type}`}
      {...rest}
    >
      {icons[type]}

      <div className="grid gap-sm self-center">
        {title && <div className="font-body-bold text-inherit!">{title}</div>}

        <div>{children}</div>
      </div>
    </div>
  );
};
